// Advanced DataGrid — direct port of yeni-design/RaporGor.html DataGrid (line 524-1092).
// Features: quick filter, sortable columns (TR locale), per-column filters
// (text/number/set), column visibility, density toggle, multi-column grouping
// with collapsible nested rows + per-group aggregation, sticky first column,
// pagination, aggregation footer, skeleton loading, empty state.
//
// AG Grid Community has no native row grouping (Enterprise-only). This is a
// hand-rolled replacement that stays Community-compatible while reproducing
// the 1:1 yeni-design look.
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { I } from './icons';
import { Input, IconButton, EmptyState, fmtTRY, fmtNum } from './primitives';

export type DataGridAlign = 'left' | 'right' | 'center';
export type DataGridFilterType = 'text' | 'number' | 'set';
export type DataGridAggType = 'sum' | 'avg' | 'count' | 'min' | 'max';

export interface DataGridColumn<R> {
  key: string;
  label: ReactNode;
  align?: DataGridAlign;
  width?: number | string;
  minWidth?: number | string;
  sortable?: boolean;
  sticky?: boolean;
  mono?: boolean;
  hideable?: boolean;
  groupable?: boolean;
  agg?: DataGridAggType;
  filter?: DataGridFilterType;
  render?: (row: R, rowIndex: number) => ReactNode;
  aggRender?: (value: number, subRows: R[]) => ReactNode;
  formatGroupValue?: (label: string) => ReactNode;
}

export interface DataGridProps<R extends Record<string, unknown>> {
  columns: DataGridColumn<R>[];
  rows: R[];
  rowKey?: keyof R;
  defaultSortKey?: string;
  defaultSortDir?: 'asc' | 'desc';
  onRowClick?: (row: R) => void;
  selectedId?: string | number | null;
  zebra?: boolean;
  loading?: boolean;
  loadingRows?: number;
  emptyTitle?: ReactNode;
  emptyBody?: ReactNode;
  density?: 'compact' | 'comfortable' | 'spacious';
  pageSize?: number;
  className?: string;
  showQuickFilter?: boolean;
  quickFilterPlaceholder?: string;
  toolbarTitle?: ReactNode;
  enableExport?: boolean;
  onExport?: () => void;
  enableDensity?: boolean;
  enableColumnPicker?: boolean;
  enableColumnFilter?: boolean;
  enablePagination?: boolean;
  defaultGroupBy?: string[];
  enableGrouping?: boolean;
}

type SetFilterValue = { __type: 'set'; values: Set<string> };
type NumberRangeValue = [number | null, number | null];
type ColFilterValue = string | NumberRangeValue | SetFilterValue;

interface GroupNode<R> {
  __group: true;
  groupKey: string;
  label: string;
  depth: number;
  count: number;
  agg: Record<string, number>;
  children: GroupOrRow<R>[];
  path?: string;
}

type GroupOrRow<R> = R | GroupNode<R>;

function isGroupNode<R>(v: unknown): v is GroupNode<R> {
  return typeof v === 'object' && v !== null && (v as GroupNode<R>).__group === true;
}

function getRowValue<R extends Record<string, unknown>>(row: R, key: string): unknown {
  return row[key];
}

export function DataGrid<R extends Record<string, unknown>>({
  columns,
  rows,
  defaultSortKey,
  defaultSortDir = 'asc',
  rowKey = 'id' as keyof R,
  onRowClick,
  selectedId,
  zebra = true,
  loading = false,
  loadingRows = 8,
  emptyTitle = 'Kayıt bulunamadı',
  emptyBody,
  density: densityProp,
  pageSize = 25,
  className = '',
  showQuickFilter = true,
  quickFilterPlaceholder = 'Tabloda ara...',
  toolbarTitle,
  enableExport = true,
  onExport,
  enableDensity = true,
  enableColumnPicker = true,
  enableColumnFilter = true,
  enablePagination = true,
  defaultGroupBy = [],
  enableGrouping = true,
}: DataGridProps<R>) {
  const [sortKey, setSortKey] = useState<string | undefined>(defaultSortKey ?? columns[0]?.key);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultSortDir);
  const [quick, setQuick] = useState('');
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [density, setDensity] = useState<'compact' | 'comfortable' | 'spacious'>(
    densityProp ?? 'comfortable',
  );
  const [page, setPage] = useState(1);
  const [colMenu, setColMenu] = useState(false);
  const [filterMenu, setFilterMenu] = useState(false);
  const [groupMenu, setGroupMenu] = useState(false);
  const [colFilters, setColFilters] = useState<Record<string, ColFilterValue>>({});
  const [groupBy, setGroupBy] = useState<string[]>(defaultGroupBy);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  // Reset to page 1 when filters / grouping change.
  useEffect(() => {
    setPage(1);
  }, [quick, colFilters, groupBy]);

  // Visible columns exclude grouped-by ones (rendered in group row instead).
  const visibleCols = columns.filter((c) => !hidden.has(c.key) && !groupBy.includes(c.key));
  const groupableCols = columns.filter((c) => c.groupable !== false);

  // Distinct values per column (set filter UI).
  const distinctVals = useMemo(() => {
    const out: Record<string, string[]> = {};
    columns.forEach((c) => {
      const set = new Set<string>();
      rows.forEach((r) => set.add(String(getRowValue(r, c.key) ?? '— Boş —')));
      out[c.key] = Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
    });
    return out;
  }, [rows, columns]);

  // Filtering — quick search + per-column filters.
  const filtered = useMemo(() => {
    let r = rows;
    if (quick) {
      const q = quick.toLocaleLowerCase('tr');
      r = r.filter((row) =>
        columns.some((c) => {
          const v = getRowValue(row, c.key);
          return v != null && String(v).toLocaleLowerCase('tr').includes(q);
        }),
      );
    }
    Object.entries(colFilters).forEach(([k, v]) => {
      if (!v) return;
      r = r.filter((row) => {
        const cv = getRowValue(row, k);
        if (typeof v === 'object' && '__type' in v && v.__type === 'set') {
          return v.values.has(String(cv ?? '— Boş —'));
        }
        if (Array.isArray(v)) {
          const [mn, mx] = v;
          const num = typeof cv === 'number' ? cv : Number(cv);
          if (mn != null && num < mn) return false;
          if (mx != null && num > mx) return false;
          return true;
        }
        return String(cv ?? '')
          .toLocaleLowerCase('tr')
          .includes(String(v).toLocaleLowerCase('tr'));
      });
    });
    return r;
  }, [rows, quick, colFilters, columns]);

  // Sorting — TR locale for strings, numeric otherwise.
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const aV = getRowValue(a, sortKey);
      const bV = getRowValue(b, sortKey);
      if (aV == null) return 1;
      if (bV == null) return -1;
      if (typeof aV === 'string') {
        return sortDir === 'asc'
          ? aV.localeCompare(String(bV), 'tr')
          : String(bV).localeCompare(aV, 'tr');
      }
      const aN = Number(aV);
      const bN = Number(bV);
      return sortDir === 'asc' ? aN - bN : bN - aN;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  // Pagination (disabled when grouping).
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageRows = enablePagination ? sorted.slice((page - 1) * pageSize, page * pageSize) : sorted;

  const aggregateRows = useCallback(
    (subRows: R[]): Record<string, number> => {
      const out: Record<string, number> = {};
      columns.forEach((c) => {
        if (!c.agg) return;
        const vals = subRows.map((r) => Number(getRowValue(r, c.key)) || 0);
        switch (c.agg) {
          case 'sum':
            out[c.key] = vals.reduce((a, b) => a + b, 0);
            break;
          case 'avg':
            out[c.key] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
            break;
          case 'count':
            out[c.key] = subRows.length;
            break;
          case 'min':
            out[c.key] = vals.length ? Math.min(...vals) : 0;
            break;
          case 'max':
            out[c.key] = vals.length ? Math.max(...vals) : 0;
            break;
        }
      });
      return out;
    },
    [columns],
  );

  // Build nested group tree.
  const groupTree = useMemo<GroupNode<R>[] | null>(() => {
    if (groupBy.length === 0) return null;
    const build = (rs: R[], depth: number): GroupOrRow<R>[] => {
      if (depth >= groupBy.length) return rs;
      const key = groupBy[depth];
      const map = new Map<string, R[]>();
      rs.forEach((r) => {
        const v = getRowValue(r, key);
        const label = v == null ? '— Boş —' : String(v);
        if (!map.has(label)) map.set(label, []);
        map.get(label)!.push(r);
      });
      const arr = Array.from(map.entries()).sort((a, b) =>
        a[0].localeCompare(b[0], 'tr'),
      );
      return arr.map(
        ([label, subRows]): GroupNode<R> => ({
          __group: true,
          groupKey: key,
          label,
          depth,
          count: subRows.length,
          agg: aggregateRows(subRows),
          children: build(subRows, depth + 1),
        }),
      );
    };
    return build(sorted, 0) as GroupNode<R>[];
  }, [sorted, groupBy, aggregateRows]);

  // Flatten group tree honoring `collapsed`.
  const flattened = useMemo<GroupOrRow<R>[] | null>(() => {
    if (!groupTree) return null;
    const out: GroupOrRow<R>[] = [];
    const walk = (nodes: GroupOrRow<R>[], parentPath = '') => {
      nodes.forEach((n) => {
        if (isGroupNode<R>(n)) {
          const path = parentPath + '|' + n.groupKey + '=' + n.label;
          out.push({ ...n, path });
          if (!collapsed.has(path)) {
            walk(n.children, path);
          }
        } else {
          out.push(n);
        }
      });
    };
    walk(groupTree);
    return out;
  }, [groupTree, collapsed]);

  // Density mappings.
  const densityRow =
    density === 'compact' ? 'h-8' : density === 'spacious' ? 'h-14' : 'h-11';
  const densityHead =
    density === 'compact' ? 'h-8' : density === 'spacious' ? 'h-10' : 'h-9';
  const densityText =
    density === 'compact' ? 'text-[12px]' : density === 'spacious' ? 'text-[13.5px]' : 'text-[13px]';

  // Aggregation footer (whole sorted set).
  const aggregates = useMemo(() => {
    const out: Record<string, number> = {};
    columns.forEach((c) => {
      if (!c.agg) return;
      if (c.agg === 'sum')
        out[c.key] = sorted.reduce((s, r) => s + (Number(getRowValue(r, c.key)) || 0), 0);
      else if (c.agg === 'avg' && sorted.length)
        out[c.key] =
          sorted.reduce((s, r) => s + (Number(getRowValue(r, c.key)) || 0), 0) / sorted.length;
      else if (c.agg === 'count') out[c.key] = sorted.length;
    });
    return out;
  }, [sorted, columns]);

  const handleSort = (k: string) => {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir('asc');
    }
  };
  const toggleCol = (k: string) =>
    setHidden((h) => {
      const n = new Set(h);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const toggleGroupCol = (k: string) =>
    setGroupBy((g) => (g.includes(k) ? g.filter((x) => x !== k) : [...g, k]));
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => {
    if (!groupTree) return;
    const all = new Set<string>();
    const walk = (nodes: GroupOrRow<R>[], parent = '') => {
      nodes.forEach((n) => {
        if (isGroupNode<R>(n)) {
          const path = parent + '|' + n.groupKey + '=' + n.label;
          all.add(path);
          walk(n.children, path);
        }
      });
    };
    walk(groupTree);
    setCollapsed(all);
  };
  const toggleGroup = (path: string) =>
    setCollapsed((c) => {
      const n = new Set(c);
      if (n.has(path)) n.delete(path);
      else n.add(path);
      return n;
    });

  const activeFilterCount = Object.values(colFilters).filter((v) => {
    if (!v) return false;
    if (typeof v === 'object' && '__type' in v) return true;
    if (Array.isArray(v)) return v.some((x) => x != null);
    return v.length > 0;
  }).length;

  const renderRows: GroupOrRow<R>[] = groupBy.length === 0 ? pageRows : (flattened ?? []);

  return (
    <div
      className={`bg-surface border border-border rounded-lg overflow-hidden flex flex-col ${className}`}
    >
      {/* Toolbar */}
      <div className="px-3 py-2.5 border-b border-border flex items-center gap-2 flex-wrap bg-ink-50/60">
        {toolbarTitle && (
          <span className="text-[12px] font-semibold text-ink-700 mr-2 whitespace-nowrap">
            {toolbarTitle}
          </span>
        )}
        {showQuickFilter && (
          <Input
            icon={I.Search}
            placeholder={quickFilterPlaceholder}
            size="sm"
            wrapClass="w-[260px]"
            value={quick}
            onChange={(e) => setQuick(e.target.value)}
          />
        )}
        {/* flex-wrap + min-w-0: mobilde (346px) grup tek parça taşıp kırpılıyordu */}
        <div className="flex items-center gap-1 ml-auto flex-wrap min-w-0 max-w-full">
          {enableGrouping && (
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setGroupMenu((m) => !m);
                  setColMenu(false);
                  setFilterMenu(false);
                }}
                className={`focus-ring inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[12px] font-medium border ${
                  groupBy.length
                    ? 'bg-brand-50 border-brand-200 text-brand-700'
                    : 'bg-white border-border text-ink-700 hover:bg-ink-50'
                }`}
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M2 4h12M4 8h10M6 12h8" />
                </svg>
                Grup
                {groupBy.length > 0 && (
                  <span className="bg-brand-700 text-white rounded text-[10px] px-1 mono">
                    {groupBy.length}
                  </span>
                )}
              </button>
              {groupMenu && (
                <div className="absolute right-0 top-9 z-30 bg-white border border-border rounded-md shadow-lg p-2 w-[260px]">
                  <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold px-1.5 py-1.5 flex items-center justify-between">
                    <span>Grupla</span>
                    {groupBy.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setGroupBy([])}
                        className="text-brand-700 normal-case tracking-normal font-medium"
                      >
                        Temizle
                      </button>
                    )}
                  </div>
                  {groupBy.length > 0 && (
                    <div className="px-1.5 pb-2 mb-1 border-b border-border">
                      <div className="text-[10.5px] text-ink-500 mb-1.5">Sıralı gruplar:</div>
                      <div className="flex flex-wrap gap-1">
                        {groupBy.map((k, i) => {
                          const c = columns.find((x) => x.key === k);
                          return (
                            <span
                              key={k}
                              className="inline-flex items-center gap-1 bg-brand-50 border border-brand-200 text-brand-700 rounded text-[11.5px] px-1.5 py-0.5 font-medium"
                            >
                              <span className="mono text-brand-600 text-[10px]">{i + 1}.</span>
                              {c?.label || k}
                              <button
                                type="button"
                                onClick={() => toggleGroupCol(k)}
                                className="hover:text-brand-900"
                              >
                                <I.X size={10} strokeWidth={2.5} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {groupableCols.map((c) => (
                    <label
                      key={c.key}
                      className="flex items-center gap-2 px-1.5 py-1.5 rounded hover:bg-ink-50 cursor-pointer"
                    >
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          toggleGroupCol(c.key);
                        }}
                        className={`w-4 h-4 rounded border flex items-center justify-center ${
                          groupBy.includes(c.key)
                            ? 'bg-brand-700 border-brand-700'
                            : 'bg-white border-border-strong'
                        }`}
                      >
                        {groupBy.includes(c.key) && (
                          <I.Check size={9} className="text-white" strokeWidth={3} />
                        )}
                      </span>
                      <span className="text-[12px] text-ink-700">{c.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          {enableColumnFilter && (
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setFilterMenu((m) => !m);
                  setColMenu(false);
                  setGroupMenu(false);
                }}
                className={`focus-ring inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[12px] font-medium border ${
                  activeFilterCount
                    ? 'bg-brand-50 border-brand-200 text-brand-700'
                    : 'bg-white border-border text-ink-700 hover:bg-ink-50'
                }`}
              >
                <I.Filter size={12} /> Filtre
                {activeFilterCount > 0 && (
                  <span className="bg-brand-700 text-white rounded text-[10px] px-1 mono">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              {filterMenu && (
                <div className="absolute right-0 top-9 z-30 bg-white border border-border rounded-md shadow-lg p-3 w-[280px]">
                  <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold mb-2">
                    Sütun Filtreleri
                  </div>
                  {columns
                    .filter((c) => c.filter)
                    .map((c) => (
                      <div key={c.key} className="mb-2">
                        <label className="text-[12px] text-ink-700 font-medium block mb-1">
                          {c.label}
                        </label>
                        {c.filter === 'text' && (
                          <input
                            type="text"
                            value={typeof colFilters[c.key] === 'string' ? (colFilters[c.key] as string) : ''}
                            onChange={(e) =>
                              setColFilters((f) => ({ ...f, [c.key]: e.target.value }))
                            }
                            className="w-full bg-white border border-border rounded h-7 text-[12px] px-2 focus-ring focus:border-brand-600 outline-none"
                            placeholder={`${c.label} içerir...`}
                          />
                        )}
                        {c.filter === 'number' && (
                          <div className="flex gap-1.5">
                            <input
                              type="number"
                              placeholder="min"
                              value={
                                Array.isArray(colFilters[c.key])
                                  ? ((colFilters[c.key] as NumberRangeValue)[0] ?? '')
                                  : ''
                              }
                              onChange={(e) =>
                                setColFilters((f) => {
                                  const cur = Array.isArray(f[c.key])
                                    ? (f[c.key] as NumberRangeValue)
                                    : ([null, null] as NumberRangeValue);
                                  return {
                                    ...f,
                                    [c.key]: [
                                      e.target.value === '' ? null : +e.target.value,
                                      cur[1],
                                    ] as NumberRangeValue,
                                  };
                                })
                              }
                              className="w-full bg-white border border-border rounded h-7 text-[12px] px-2 mono focus-ring focus:border-brand-600 outline-none"
                            />
                            <input
                              type="number"
                              placeholder="max"
                              value={
                                Array.isArray(colFilters[c.key])
                                  ? ((colFilters[c.key] as NumberRangeValue)[1] ?? '')
                                  : ''
                              }
                              onChange={(e) =>
                                setColFilters((f) => {
                                  const cur = Array.isArray(f[c.key])
                                    ? (f[c.key] as NumberRangeValue)
                                    : ([null, null] as NumberRangeValue);
                                  return {
                                    ...f,
                                    [c.key]: [
                                      cur[0],
                                      e.target.value === '' ? null : +e.target.value,
                                    ] as NumberRangeValue,
                                  };
                                })
                              }
                              className="w-full bg-white border border-border rounded h-7 text-[12px] px-2 mono focus-ring focus:border-brand-600 outline-none"
                            />
                          </div>
                        )}
                        {c.filter === 'set' && (
                          <div className="border border-border rounded max-h-[140px] overflow-y-auto thin-scroll p-1">
                            {distinctVals[c.key].map((v) => {
                              const cur = colFilters[c.key];
                              const set =
                                cur && typeof cur === 'object' && '__type' in cur
                                  ? cur.values
                                  : new Set(distinctVals[c.key]);
                              const checked = set.has(v);
                              return (
                                <label
                                  key={v}
                                  className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-ink-50 cursor-pointer text-[12px] text-ink-700"
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() =>
                                      setColFilters((f) => {
                                        const cv = f[c.key];
                                        const base =
                                          cv && typeof cv === 'object' && '__type' in cv
                                            ? new Set(cv.values)
                                            : new Set(distinctVals[c.key]);
                                        if (base.has(v)) base.delete(v);
                                        else base.add(v);
                                        return {
                                          ...f,
                                          [c.key]: { __type: 'set', values: base } as SetFilterValue,
                                        };
                                      })
                                    }
                                    className="accent-brand-700"
                                  />
                                  <span className="truncate">{v}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setColFilters({})}
                      className="text-[12px] font-medium text-brand-700 hover:text-brand-600 mt-1"
                    >
                      Filtreleri temizle
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {enableColumnPicker && (
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setColMenu((m) => !m);
                  setFilterMenu(false);
                  setGroupMenu(false);
                }}
                className="focus-ring inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[12px] font-medium border bg-white border-border text-ink-700 hover:bg-ink-50"
              >
                <I.Sidebar size={12} /> Sütunlar
              </button>
              {colMenu && (
                <div className="absolute right-0 top-9 z-30 bg-white border border-border rounded-md shadow-lg p-2 w-[220px]">
                  <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold px-1.5 py-1">
                    Görünür Sütunlar
                  </div>
                  {columns
                    .filter((c) => c.hideable !== false)
                    .map((c) => (
                      <label
                        key={c.key}
                        className="flex items-center gap-2 px-1.5 py-1.5 rounded hover:bg-ink-50 cursor-pointer"
                      >
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            toggleCol(c.key);
                          }}
                          className={`w-4 h-4 rounded border flex items-center justify-center ${
                            !hidden.has(c.key)
                              ? 'bg-brand-700 border-brand-700'
                              : 'bg-white border-border-strong'
                          }`}
                        >
                          {!hidden.has(c.key) && (
                            <I.Check size={9} className="text-white" strokeWidth={3} />
                          )}
                        </span>
                        <span className="text-[12px] text-ink-700">{c.label}</span>
                      </label>
                    ))}
                </div>
              )}
            </div>
          )}
          {enableDensity && (
            <div className="inline-flex items-center bg-ink-100 rounded-md p-0.5">
              {(
                [
                  { id: 'compact', label: '1x' },
                  { id: 'comfortable', label: '2x' },
                  { id: 'spacious', label: '3x' },
                ] as const
              ).map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setDensity(d.id)}
                  title={d.id}
                  className={`focus-ring h-6 w-7 rounded text-[10px] font-semibold mono ${
                    density === d.id ? 'seg-active' : 'text-ink-500 hover:text-ink-900'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          )}
          {enableExport && (
            <>
              <span className="w-px h-5 bg-border mx-1" />
              <IconButton
                icon={I.Download}
                label="Excel"
                size="sm"
                variant="border"
                onClick={onExport}
              />
              <IconButton
                icon={I.Print}
                label="Yazdır"
                size="sm"
                variant="border"
                onClick={() => window.print()}
              />
            </>
          )}
        </div>
      </div>

      {/* Active groups bar */}
      {groupBy.length > 0 && (
        <div className="px-3 py-2 border-b border-border bg-brand-50/40 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-brand-700 font-semibold">
            Gruplama
          </span>
          <div className="flex items-center gap-1 flex-wrap">
            {groupBy.map((k, i) => {
              const c = columns.find((x) => x.key === k);
              return (
                <Fragment key={k}>
                  {i > 0 && <I.Chevron size={10} className="text-ink-400" />}
                  <span className="inline-flex items-center gap-1 bg-white border border-brand-200 text-brand-700 rounded-md text-[12px] px-2 py-0.5 font-medium shadow-sm">
                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 4h12M4 8h10M6 12h8" />
                    </svg>
                    {c?.label || k}
                    <button
                      type="button"
                      onClick={() => toggleGroupCol(k)}
                      className="text-brand-500 hover:text-brand-900"
                    >
                      <I.X size={10} strokeWidth={2.5} />
                    </button>
                  </span>
                </Fragment>
              );
            })}
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={expandAll}
              className="text-[11.5px] font-medium text-brand-700 hover:text-brand-900 inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-white"
            >
              <I.ChevronDown size={10} strokeWidth={2.5} /> Tümünü aç
            </button>
            <button
              type="button"
              onClick={collapseAll}
              className="text-[11.5px] font-medium text-brand-700 hover:text-brand-900 inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-white"
            >
              <I.ChevronUp size={10} strokeWidth={2.5} /> Tümünü kapa
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto thin-scroll">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {visibleCols.map((col) => {
                const sortable = col.sortable !== false;
                const isSorted = sortKey === col.key;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    style={
                      col.width
                        ? { width: col.width, minWidth: col.minWidth || col.width }
                        : col.minWidth
                          ? { minWidth: col.minWidth }
                          : undefined
                    }
                    className={`px-3 ${densityHead} text-[11px] font-semibold text-ink-600 uppercase tracking-wider whitespace-nowrap bg-ink-50 border-b border-border ${
                      col.sticky ? 'sticky-left bg-ink-50' : 'relative'
                    } ${
                      col.align === 'right'
                        ? 'text-right'
                        : col.align === 'center'
                          ? 'text-center'
                          : 'text-left'
                    } group`}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => handleSort(col.key)}
                        className="inline-flex items-center gap-1 hover:text-ink-900 bg-transparent border-0 p-0 m-0 font-inherit text-inherit cursor-pointer"
                      >
                        <span>{col.label}</span>
                        <span className="flex flex-col leading-none text-ink-400">
                          <I.ChevronUp
                            size={7}
                            strokeWidth={2.5}
                            className={isSorted && sortDir === 'asc' ? 'text-brand-700' : ''}
                          />
                          <I.ChevronDown
                            size={7}
                            strokeWidth={2.5}
                            className={isSorted && sortDir === 'desc' ? 'text-brand-700' : ''}
                          />
                        </span>
                      </button>
                    ) : (
                      col.label
                    )}
                    <span className="absolute right-0 top-2 bottom-2 w-px bg-border group-hover:bg-border-strong" />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white">
            {loading ? (
              Array.from({ length: loadingRows }).map((_, i) => (
                <tr key={i} className={zebra && i % 2 ? 'bg-ink-50/50' : 'bg-white'}>
                  {visibleCols.map((c, j) => (
                    <td
                      key={c.key}
                      className={`px-3 ${densityRow} border-b border-border`}
                      style={
                        c.sticky ? { background: 'inherit', position: 'sticky', left: 0 } : undefined
                      }
                    >
                      <div
                        className="skeleton h-3 rounded"
                        style={{ width: `${50 + ((i + j) % 4) * 12}%` }}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : renderRows.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleCols.length}
                  className="px-4 py-10 text-center bg-white border-b border-border"
                >
                  <EmptyState title={emptyTitle} body={emptyBody} icon={I.Inbox} />
                </td>
              </tr>
            ) : (
              renderRows.map((row, i) => {
                if (isGroupNode<R>(row)) {
                  const path = row.path ?? '';
                  const expanded = !collapsed.has(path);
                  const indent = row.depth * 16;
                  const groupCol = columns.find((x) => x.key === row.groupKey);
                  const headerBg = row.depth === 0 ? '#f1f5f9' : '#f8fafc';
                  return (
                    <tr
                      key={path}
                      onClick={() => toggleGroup(path)}
                      className="cursor-pointer hover:bg-brand-50/60"
                      style={{ background: headerBg }}
                    >
                      {visibleCols.map((col, ci) => (
                        <td
                          key={col.key}
                          className={`px-3 ${densityRow} ${densityText} font-semibold text-ink-900 border-b border-border whitespace-nowrap ${
                            col.align === 'right'
                              ? 'text-right'
                              : col.align === 'center'
                                ? 'text-center'
                                : 'text-left'
                          } ${col.mono ? 'mono tnum' : ''} ${col.sticky ? 'sticky-left' : ''}`}
                          style={col.sticky ? { background: headerBg } : undefined}
                        >
                          {ci === 0 ? (
                            <div className="flex items-center gap-1.5" style={{ paddingLeft: indent }}>
                              <button
                                type="button"
                                className="w-4 h-4 flex items-center justify-center rounded hover:bg-white text-ink-600"
                              >
                                {expanded ? (
                                  <I.ChevronDown size={11} strokeWidth={2.5} />
                                ) : (
                                  <I.Chevron size={11} strokeWidth={2.5} />
                                )}
                              </button>
                              <span className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold">
                                {groupCol?.label || row.groupKey}:
                              </span>
                              <span className="text-ink-900">
                                {groupCol?.formatGroupValue
                                  ? groupCol.formatGroupValue(row.label)
                                  : row.label}
                              </span>
                              <span className="ml-1 inline-flex items-center bg-white border border-border text-ink-600 rounded text-[10.5px] px-1.5 mono tnum">
                                {row.count}
                              </span>
                            </div>
                          ) : row.agg[col.key] != null ? (
                            col.aggRender ? (
                              col.aggRender(row.agg[col.key], row.children as R[])
                            ) : col.agg === 'sum' || col.agg === 'avg' ? (
                              fmtTRY(row.agg[col.key], { decimals: 0 })
                            ) : (
                              fmtNum(row.agg[col.key])
                            )
                          ) : null}
                        </td>
                      ))}
                    </tr>
                  );
                }
                const idVal = row[rowKey];
                const sel =
                  selectedId != null &&
                  (idVal === selectedId || String(idVal) === String(selectedId));
                const baseBg = sel ? '#eff6ff' : zebra && i % 2 ? '#f8fafc' : '#fff';
                const leafIndent = groupBy.length * 16 + 20;
                return (
                  <tr
                    key={String(idVal ?? i)}
                    onClick={() => onRowClick && onRowClick(row)}
                    className={`row-hover ${onRowClick ? 'cursor-pointer' : ''} ${sel ? 'row-selected' : ''} ${
                      zebra && i % 2 ? 'bg-ink-50/50' : 'bg-white'
                    }`}
                  >
                    {visibleCols.map((col, ci) => (
                      <td
                        key={col.key}
                        className={`px-3 ${densityRow} ${densityText} text-ink-900 border-b border-border whitespace-nowrap ${
                          col.align === 'right'
                            ? 'text-right'
                            : col.align === 'center'
                              ? 'text-center'
                              : 'text-left'
                        } ${col.mono ? 'mono' : ''} ${col.sticky ? 'sticky-left' : ''} ${
                          ci === 0 && sel ? 'border-l-[3px] border-l-brand-700' : ''
                        }`}
                        style={col.sticky ? { background: baseBg } : undefined}
                      >
                        {ci === 0 && groupBy.length > 0 ? (
                          <div style={{ paddingLeft: leafIndent }}>
                            {col.render ? col.render(row, i) : (row[col.key] as ReactNode)}
                          </div>
                        ) : col.render ? (
                          col.render(row, i)
                        ) : (
                          (row[col.key] as ReactNode)
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
          {Object.keys(aggregates).length > 0 && !loading && sorted.length > 0 && (
            <tfoot>
              <tr className="bg-ink-50">
                {visibleCols.map((c, ci) => (
                  <td
                    key={c.key}
                    className={`px-3 h-10 ${densityText} font-semibold text-ink-900 border-t-2 border-border-strong ${
                      c.align === 'right'
                        ? 'text-right'
                        : c.align === 'center'
                          ? 'text-center'
                          : 'text-left'
                    } ${c.mono ? 'mono tnum' : ''} ${c.sticky ? 'sticky-left' : ''}`}
                    style={c.sticky ? { background: '#f1f5f9' } : undefined}
                  >
                    {ci === 0 && (
                      <span className="text-[11px] uppercase tracking-wider text-ink-500">
                        {groupBy.length ? `Genel Toplam (${sorted.length})` : 'Toplam'}
                      </span>
                    )}
                    {aggregates[c.key] != null &&
                      (c.aggRender
                        ? c.aggRender(aggregates[c.key], sorted)
                        : c.agg === 'sum' || c.agg === 'avg'
                          ? fmtTRY(aggregates[c.key], { decimals: 2 })
                          : fmtNum(aggregates[c.key]))}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Group footer (when grouping) */}
      {groupBy.length > 0 && !loading && (
        <div className="border-t border-border bg-ink-50/40 px-3 py-2 flex items-center justify-between text-[12px] gap-3 flex-wrap">
          <div className="text-ink-600">
            <span className="text-ink-700 font-medium tnum">{groupTree?.length ?? 0}</span>
            <span className="text-ink-500 ml-1">grup</span>
            <span className="text-ink-300 mx-2">·</span>
            <span className="text-ink-700 font-medium tnum">{sorted.length}</span>
            <span className="text-ink-500 ml-1">satır</span>
          </div>
          <span className="text-[11px] text-ink-500">
            Sayfalama gruplandırma sırasında devre dışı
          </span>
        </div>
      )}

      {/* Pagination footer */}
      {enablePagination && groupBy.length === 0 && (
        <div className="border-t border-border bg-ink-50/40 px-3 py-2 flex items-center justify-between text-[12px] gap-3 flex-wrap">
          <div className="text-ink-600">
            <span className="text-ink-700 font-medium tnum">
              {(page - 1) * pageSize + 1}
            </span>
            <span className="text-ink-400 mx-1">–</span>
            <span className="text-ink-700 font-medium tnum">
              {Math.min(page * pageSize, sorted.length)}
            </span>
            <span className="text-ink-500 mx-1">/</span>
            <span className="text-ink-700 font-medium tnum">{sorted.length}</span>
            <span className="text-ink-500 ml-1">kayıt</span>
            {(quick || activeFilterCount > 0) && rows.length !== sorted.length && (
              <span className="ml-2 text-ink-400">({rows.length} kayıt arasından)</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="focus-ring p-1.5 rounded hover:bg-ink-100 disabled:opacity-30 disabled:cursor-not-allowed text-ink-600"
              aria-label="İlk sayfa"
            >
              <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 4v8M6 8l4-4M6 8l4 4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="focus-ring p-1.5 rounded hover:bg-ink-100 disabled:opacity-30 disabled:cursor-not-allowed text-ink-600"
              aria-label="Önceki sayfa"
            >
              <I.Chevron size={11} className="rotate-180" />
            </button>
            <span className="text-[12px] text-ink-700 px-2 mono tnum">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="focus-ring p-1.5 rounded hover:bg-ink-100 disabled:opacity-30 disabled:cursor-not-allowed text-ink-600"
              aria-label="Sonraki sayfa"
            >
              <I.Chevron size={11} />
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="focus-ring p-1.5 rounded hover:bg-ink-100 disabled:opacity-30 disabled:cursor-not-allowed text-ink-600"
              aria-label="Son sayfa"
            >
              <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M13 4v8M10 8L6 4M10 8L6 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
